import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import crmService from "../api/crmService";
import notificationsService from "../api/notificationsService";
import { hasCrm, seesAllCrm } from "../access";
import {
  NOTIFICATION_REFRESH_EVENT,
  emptyPending,
  normalizePending,
} from "../utils/notificationCenter";

const POLL_MS = 60000;
const EMAIL_LIST_LIMIT = 50;

/**
 * Fuente única para la campana: seguimientos pending + resúmenes semanales.
 * El badge siempre refleja lo mismo que se puede listar en la bandeja.
 */
export function useNotificationCenter(user) {
  const isCrm = hasCrm(user);
  const canSeeEmails = seesAllCrm(user);
  const allowed = isCrm || canSeeEmails;

  const [pending, setPending] = useState(emptyPending);
  const [items, setItems] = useState([]);
  const [unreadEmails, setUnreadEmails] = useState(0);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const followUpRows = useMemo(() => {
    const urgent = (pending.urgent || []).map((lead) => ({ lead, kind: "urgent" }));
    const overdue = (pending.overdue || []).map((lead) => ({ lead, kind: "overdue" }));
    const today = (pending.today || []).map((lead) => ({ lead, kind: "today" }));
    return [...urgent, ...overdue, ...today];
  }, [pending]);

  const followUpCount = followUpRows.length;
  const emailUnreadInTray = canSeeEmails ? items.filter((it) => !it.readAt).length : 0;
  const badgeCount = followUpCount + emailUnreadInTray;

  const refresh = useCallback(
    async ({ withLoading = false } = {}) => {
      if (!allowed) return;
      const id = ++reqId.current;

      try {
        if (withLoading) setLoading(true);

        const tasks = [];
        if (isCrm) {
          tasks.push(
            crmService.getPending().then((res) => {
              if (id === reqId.current) {
                setPending(normalizePending(res.data.data));
              }
            })
          );
        } else if (id === reqId.current) {
          setPending(emptyPending());
        }

        if (canSeeEmails) {
          tasks.push(
            Promise.all([
              notificationsService.list({ limit: EMAIL_LIST_LIMIT }),
              notificationsService.list({ type: "URGENT_LEAD", limit: 20 }),
            ]).then(([weekly, urgent]) => {
              if (id !== reqId.current) return;
              const urgentItems = urgent.data.data || [];
              setUnreadEmails((weekly.data.unread ?? 0) + urgentItems.filter((it) => !it.readAt).length);
              setItems([...urgentItems, ...(weekly.data.data || [])]);
            })
          );
        } else if (id === reqId.current) {
          setItems([]);
          setUnreadEmails(0);
        }

        await Promise.all(tasks);
      } catch {
        /* silencioso */
      } finally {
        if (withLoading && id === reqId.current) setLoading(false);
      }
    },
    [allowed, isCrm, canSeeEmails]
  );

  useEffect(() => {
    if (!allowed) return undefined;

    refresh();

    const onExternalRefresh = () => refresh();
    const onFocus = () => refresh();
    const pollId = setInterval(refresh, POLL_MS);

    window.addEventListener(NOTIFICATION_REFRESH_EVENT, onExternalRefresh);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      clearInterval(pollId);
      window.removeEventListener(NOTIFICATION_REFRESH_EVENT, onExternalRefresh);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [allowed, refresh]);

  const loadTray = useCallback(() => refresh({ withLoading: true }), [refresh]);

  const markAllEmailsRead = useCallback(async () => {
    await notificationsService.markAllRead();
    setItems((prev) =>
      prev.map((it) => ({ ...it, readAt: it.readAt || new Date().toISOString() }))
    );
    setUnreadEmails(0);
  }, []);

  const markEmailRead = useCallback((id) => {
    setItems((prev) =>
      prev.map((it) =>
        it._id === id ? { ...it, readAt: it.readAt || new Date().toISOString() } : it
      )
    );
    setUnreadEmails((u) => Math.max(0, u - 1));
  }, []);

  return {
    allowed,
    isCrm,
    canSeeEmails,
    pending,
    followUpRows,
    followUpCount,
    items,
    unreadEmails,
    emailUnreadInTray,
    badgeCount,
    loading,
    loadTray,
    refresh,
    markAllEmailsRead,
    markEmailRead,
  };
}
